import type {
  MaterialDetailAggrItem,
  MaterialTextRewriteRawResult,
} from '#/api/modules/material-analysis/materials'
import type { MaterialTranslateAlgoZhStatusValue } from '#/api/modules/material-analysis/translate-algo-zh'

/**
 * 素材分析模块的一级品类白名单。
 * 这里作为统一数据源，筛选项、上传表单和账号管理都会基于它派生选项，避免各页面各自维护字符串。
 */
export const MATERIAL_CATEGORIES = [
  '3D瑜伽裤',
  'POD',
  '下巴提拉贴',
  '丝绸内裤',
  '二氧化碳检测器',
  '健康鞋垫',
  '助听耳机',
  '压力袜',
  '压力裤',
  '吊坠',
  '壁灯',
  '宠物救助手镯',
  '宠物捐赠',
  '宠物磨甲器',
  '旅行包',
  '文胸',
  '指甲真菌治疗仪',
  '汽车坐垫',
  '水龙头过滤器',
  '牙龈治疗仪',
  '猫抓板',
  '男士内裤',
  '皮肤护理',
  '发夹',
  '眼袋贴片',
  '眼袋治疗仪',
  '矫形鞋',
  '红光治疗仪',
  '粉底液',
  '袜子',
  '孕妈服装',
  '足部治疗仪',
  '宠物用品',
] as const

export type MaterialCategory = (typeof MATERIAL_CATEGORIES)[number]

export type MaterialPlatform = 'Meta' | 'TikTok'

export type MaterialStatus = '分析中' | '已分析' | '未分析'
export type MaterialUploadType = 'auto' | 'manual'
export type MaterialMetricValue = number | string

export interface MaterialAlgoAnalysisEvidence {
  text?: string
  visual?: string
}

export interface MaterialAlgoAnalysisUsp {
  claim?: {
    normalized?: string
    raw_text?: string
  }
  type?: string
}

export interface MaterialAlgoAnalysisPainPoint {
  description?: string
}

export interface MaterialAlgoAnalysisProofItem {
  evidence?: MaterialAlgoAnalysisEvidence | string
}

export interface MaterialAlgoAnalysisEfficacyClaim {
  claim?: string
  risk?: string
}

export interface MaterialAlgoAnalysisUrgencyItem {
  evidence?: MaterialAlgoAnalysisEvidence | string
}

export type MaterialAlgoAnalysisHookPresentation =
  | string
  | {
      name?: string
    }

export interface MaterialAlgoAnalysisResult {
  cognition?: {
    detail_track?: {
      segments?: Array<{
        content_summary?: string
        end_time?: string
        id?: string
        muted_video_url?: string
        role?: string
        start_time?: string
        video_url?: string
      }>
      business_details?: {
        cta?: {
          text?: string
        }
        urgency?: {
          details?: MaterialAlgoAnalysisUrgencyItem[]
        }
      }
      efficacy_claims?: MaterialAlgoAnalysisEfficacyClaim[]
      pain_points?: MaterialAlgoAnalysisPainPoint[]
      social_proof?: {
        details?: MaterialAlgoAnalysisProofItem[]
      }
      trust_signals?: {
        details?: MaterialAlgoAnalysisProofItem[]
      }
      usps?: MaterialAlgoAnalysisUsp[]
    }
    fast_track?: {
      content?: {
        theme?: string
      }
      format?: {
        copy_hook?: {
          presentations?: MaterialAlgoAnalysisHookPresentation[]
        }
        hook_type?: string
        visual_hook?: {
          presentations?: MaterialAlgoAnalysisHookPresentation[]
        }
      }
    }
  }
  perception?: {
    audio_facts?: {
      music_energy?: string
      music_style?: string
      sound_effects?: string[]
      speech_rate?: string
      speech_type?: string
      speech_transcript?: {
        full_text?: string
        key_phrases?: string[]
      }
    }
    temporal_facts?: {
      avg_scene_duration?: number
      duration_sec?: number
      scene_count?: number
    }
    visual_facts?: {
      environment?: string
      lighting?: string
      production_style_hint?: string
      special_effects?: string[]
      subjects?: string[]
      visual_rhythm?: string
    }
  }
}

export enum AlgoAnalysisStatusEnum {
  FAILURE = 'failure',
  IN_PROCESS = 'in_process',
  PENDING = 'pending',
  SUCCESS = 'success',
}

export interface MaterialItem {
  id: string
  /** SR 联合主键中的媒体平台。所有素材操作都需要它与 id/materialId 一起回传后端。 */
  mediaPlatform?: string
  platform: MaterialPlatform
  category: string
  algoAnalysisStatus: AlgoAnalysisStatusEnum
  wasAnalyzedBefore?: boolean
  status: MaterialStatus
  uploadType: MaterialUploadType
  source?: 'platform_sync' | 'user_upload'
  storageUrl?: string
  cover: string
  dailyReportDates?: string[]
  dailyActiveAds: number[]
  dailyTotalAds: number[]
  dailySpend: number[]
  dailyRoas: number[]
  activeAdsCount?: MaterialMetricValue
  totalAdsCount?: MaterialMetricValue
  spend?: MaterialMetricValue
  roas?: MaterialMetricValue
  conversionValue?: MaterialMetricValue
  conversions?: MaterialMetricValue
  impressions?: MaterialMetricValue
  clicks?: MaterialMetricValue
  cpa?: MaterialMetricValue
  cpc?: MaterialMetricValue
  cpm?: MaterialMetricValue
  ctr?: MaterialMetricValue
  cvr?: MaterialMetricValue
  videoPlayActions?: MaterialMetricValue
  videoThruplayWatchedActions?: MaterialMetricValue
  videoP25WatchedActions?: MaterialMetricValue
  videoP50WatchedActions?: MaterialMetricValue
  videoP75WatchedActions?: MaterialMetricValue
  videoP95WatchedActions?: MaterialMetricValue
  videoP100WatchedActions?: MaterialMetricValue
  launchTime?: string
  algoCompleteTime?: string
  aggrList?: MaterialDetailAggrItem[]
  algoAnalysisResult?: MaterialAlgoAnalysisResult | null
  algoAnalysisZhResult?: MaterialAlgoAnalysisResult | null
  algoAnalysisZhStatus?: MaterialTranslateAlgoZhStatusValue | string
  algoRewriteResult?: MaterialTextRewriteRawResult[] | null
}
